import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { isLink, logger } from 'shared/lib'
import { KnowledgeBaseApi } from 'entities/KnowledgeBase'
import {
  INVALID_GOOGLE_LINK_ERROR,
  LIVE_STORAGE_IS_FULL,
  MAX_FILES,
  MAX_LIVE_DOCUMENTS,
  STORAGE_IS_FULL,
} from 'pages/settings/pages/knowledgeBase/lib/constants'
import { type KnowledgeBaseDocumentsStore } from './knowledgeBaseDocumentsStore'

export class KnowledgeBaseUrlStore {
  private _url = ''
  private _isUploading = false
  private _isUploadViaUrlOpen = false
  private _isUploadViaGoogleOpen = false
  private _errorMessages: Record<string, string> = {}

  constructor(private _documentsStore: KnowledgeBaseDocumentsStore) {
    makeAutoObservable(this)
  }

  get url() {
    return this._url
  }

  get isUploading() {
    return this._isUploading
  }

  get isUploadViaUrlOpen() {
    return this._isUploadViaUrlOpen
  }

  get isUploadViaGoogleOpen() {
    return this._isUploadViaGoogleOpen
  }

  get errorMessages() {
    return this._errorMessages
  }

  setIsUploadViaUrlOpen = (isUploadViaUrlOpen: boolean) => {
    this._isUploadViaUrlOpen = isUploadViaUrlOpen
    if (!isUploadViaUrlOpen) {
      this._url = ''
      this.setErrorMessage('url', '')
    }
  }

  setIsUploadViaGoogleOpen = (isUploadViaGoogleOpen: boolean) => {
    this._isUploadViaGoogleOpen = isUploadViaGoogleOpen
    if (!isUploadViaGoogleOpen) {
      this._url = ''
      this.setErrorMessage('url', '')
    }
  }

  setUrl = (url: string) => {
    this._url = url

    if (this._errorMessages.url) {
      this.setErrorMessage('url', '')
    }
  }

  setErrorMessage = (key: string, error: string) => {
    this._errorMessages[key] = error
  }

  handleImportUrl = async () => {
    const isUrl = isLink(this._url)
    const enterValidText = 'Please enter a valid file URL'

    if (!isUrl) {
      this.setErrorMessage('url', enterValidText)
      return
    }

    const knowledgeBase = this._documentsStore.knowledgeBase

    if (knowledgeBase && knowledgeBase.documentsCount === MAX_FILES) {
      this._documentsStore.notificationStore.addError(STORAGE_IS_FULL)
      return
    }

    try {
      runInAction(() => {
        this._isUploading = true
      })

      await KnowledgeBaseApi.importWebUrl(this.url)
      void this._documentsStore.loadDocuments()
      this.setIsUploadViaUrlOpen(false)
    } catch (e) {
      if (e instanceof AxiosError) {
        const fieldError = e.errors.getError('url')
        const messageError = e.errors.getError('message')
        this.setErrorMessage(
          'url',
          fieldError || messageError || 'Import failed. Please check the URL and try again.'
        )
      }
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isUploading = false
      })
    }
  }

  handleImportGoogleLink = async () => {
    if (!isLink(this._url)) {
      this.setErrorMessage('url', INVALID_GOOGLE_LINK_ERROR)
      return
    }

    const knowledgeBase = this._documentsStore.knowledgeBase
    if (knowledgeBase && knowledgeBase.liveDocumentsCount >= MAX_LIVE_DOCUMENTS) {
      this.setErrorMessage('url', LIVE_STORAGE_IS_FULL)
      return
    }
    if (knowledgeBase && knowledgeBase.documentsCount === MAX_FILES) {
      this._documentsStore.notificationStore.addError(STORAGE_IS_FULL)
      return
    }

    try {
      runInAction(() => {
        this._isUploading = true
      })

      await KnowledgeBaseApi.importLiveDocument(this._url)
      void this._documentsStore.loadDocuments()
      this.setIsUploadViaGoogleOpen(false)
    } catch (e) {
      if (e instanceof AxiosError) {
        const fieldError = e.errors.getError('document_url')
        const messageError = e.errors.getError('message')
        this.setErrorMessage('url', fieldError || messageError || INVALID_GOOGLE_LINK_ERROR)
      }
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isUploading = false
      })
    }
  }

  handleResyncUrl = async (id: number) => {
    try {
      await KnowledgeBaseApi.resyncUrl(id)
      void this._documentsStore.loadDocuments()
    } catch (e) {
      logger.error(e)
    }
  }
}
